import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { campusApi, eventAreaApi, assetApi, bookingApi, settingsApi } from '../services/api';
import { PageHead, Card, FormGroup, Input, Select, Textarea, Btn, Spinner, Toast } from '../components/UI';

const SLOT_STEP_MINUTES = 30;
const SLOT_WINDOW_START = '08:00';
const SLOT_WINDOW_END = '22:00';

export default function NewBookingPage() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const isTeacherUser = user?.roleName === 'Teacher';

  const [campuses,   setCampuses]   = useState([]);
  const [areas,      setAreas]      = useState([]);
  const [assets,     setAssets]     = useState([]);
  const [slotStatus, setSlotStatus] = useState({}); // { '08:00': 'Available' | 'Taken' }
  const [availableSlots, setAvailableSlots] = useState([]); // [{ slotStart, slotEnd, status }]
  const [loading,    setLoading]    = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [holidayLoading, setHolidayLoading] = useState(true);
  const [holidayRules, setHolidayRules] = useState({
    sundayClosed: true,
    secondSaturdayClosed: true,
    closedDates: new Set(),
    openDates: new Set(),
  });
  const [toast,      setToast]      = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const datePickerRef = useRef(null);

  const [form, setForm] = useState({
    campusID: user?.campusID || '',
    eventAreaID: '',
    eventName: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    expectedAttendees: '',
    purpose: '',
  });
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [qty, setQty] = useState({}); // { assetId: qty }

  // Load campuses on mount
  useEffect(() => {
    campusApi.getAll()
      .then(r => setCampuses(r.data.data || []))
      .catch(() => setCampuses([{ campusID: 1, campusName: 'Chinchwad' }, { campusID: 2, campusName: 'Hinjawadi' }]));
  }, []);

  useEffect(() => {
    settingsApi.getHolidays()
      .then((r) => {
        const d = r.data?.data || {};
        setHolidayRules({
          sundayClosed: d.sundayClosed !== false,
          secondSaturdayClosed: d.secondSaturdayClosed !== false,
          closedDates: new Set((d.closedDates || []).map(normalizeDateYmd).filter(Boolean)),
          openDates: new Set((d.openDates || []).map(normalizeDateYmd).filter(Boolean)),
        });
      })
      .catch(() => {
        // Keep defaults if holiday settings cannot be loaded.
      })
      .finally(() => setHolidayLoading(false));
  }, []);

  useEffect(() => {
    if (!showDatePicker) return;
    function onDocClick(e) {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showDatePicker]);

  // Load event areas when campus changes
  useEffect(() => {
    if (!form.campusID) return;
    eventAreaApi.getAll(form.campusID)
      .then(r => setAreas(r.data.data || []))
      .catch(() => setAreas(DEMO_AREAS.filter(a => a.campusID === parseInt(form.campusID))));
    setForm(f => ({ ...f, eventAreaID: '' }));
    setSelectedSlots([]);
  }, [form.campusID]);

  // Load assets when campus changes
  useEffect(() => {
    if (!form.campusID) return;
    assetApi.getAll(form.campusID)
      .then(r => setAssets(r.data.data || []))
      .catch(() => setAssets(DEMO_ASSETS));
  }, [form.campusID]);

  // Load slot availability when area + date both set
  useEffect(() => {
    if (!form.eventAreaID || !form.eventDate) {
      setAvailableSlots([]);
      setSlotStatus({});
      return;
    }
    setLoadingSlots(true);
    setSelectedSlots([]);
    eventAreaApi.getSlots(form.eventAreaID, form.eventDate)
      .then(r => {
        if (r?.data?.success === false) {
          setAvailableSlots([]);
          setSlotStatus({});
          setToast({ msg: r.data.message || 'Slots are not available for selected date.', type: 'error' });
          return;
        }

        let slotsFromApi = (r.data.data || [])
          .map(s => ({
            slotStart: s.slotStart,
            slotEnd: s.slotEnd || addMinutesToTime(s.slotStart, SLOT_STEP_MINUTES),
            status: s.status || s.slotStatus || 'Available',
          }))
          .sort((a, b) => a.slotStart.localeCompare(b.slotStart));

        if (!slotsFromApi.length) {
          setAvailableSlots([]);
          setSlotStatus({});
          return;
        }

        const map = {};
        slotsFromApi.forEach(s => { map[s.slotStart] = s.status; });
        setSlotStatus(map);
        setAvailableSlots(slotsFromApi);
      })
      .catch((err) => {
        setAvailableSlots([]);
        setSlotStatus({});
        setToast({
          msg: err.response?.data?.message || 'Unable to fetch slots for selected date.',
          type: 'error'
        });
      })
      .finally(() => setLoadingSlots(false));
  }, [form.eventAreaID, form.eventDate]);

  function toggleSlot(slot) {
    if (slotStatus[slot] === 'Taken') return;
    setSelectedSlots(prev =>
      prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot].sort()
    );
  }

  function setQtyFor(assetId, delta) {
    setQty(q => ({ ...q, [assetId]: Math.max(0, (parseInt(q[assetId] || 0, 10) || 0) + delta) }));
  }

  function setQtyManual(assetId, value) {
    const parsed = parseInt(value, 10);
    setQty(q => ({ ...q, [assetId]: Number.isFinite(parsed) ? Math.max(0, parsed) : 0 }));
  }

  // Derive start/end from selected slots
  const startTime = selectedSlots[0] || '';
  const endTime   = selectedSlots.length
    ? addMinutesToTime(selectedSlots[selectedSlots.length - 1], SLOT_STEP_MINUTES)
    : '';
  const durationMinutes = selectedSlots.length * SLOT_STEP_MINUTES;
  const durationLabel = durationMinutes === 0
    ? '-'
    : (durationMinutes % 60 === 0 ? `${durationMinutes / 60} hr(s)` : `${durationMinutes} min`);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.eventName.trim()) { setToast({ msg: 'Event name is required.', type: 'error' }); return; }
    if (!form.eventAreaID)       { setToast({ msg: 'Please select an event area.', type: 'error' }); return; }
    if (!form.eventDate)         { setToast({ msg: 'Please select a date.', type: 'error' }); return; }
    if (isHolidayBlocked(form.eventDate, holidayRules)) { setToast({ msg: 'Selected date is a holiday. Please choose another date.', type: 'error' }); return; }
    if (!selectedSlots.length)   { setToast({ msg: 'Please select at least one time slot.', type: 'error' }); return; }

    const payload = {
      campusID:          parseInt(form.campusID),
      eventAreaID:       parseInt(form.eventAreaID),
      eventName:         form.eventName,
      eventDate:         form.eventDate,
      startTime,
      endTime,
      expectedAttendees: form.expectedAttendees ? parseInt(form.expectedAttendees) : null,
      purpose:           form.purpose || null,
      slots: selectedSlots.map(s => ({
        slotStart: s,
        slotEnd: addMinutesToTime(s, SLOT_STEP_MINUTES),
      })),
      assets: Object.entries(qty).filter(([, v]) => (parseInt(v, 10) || 0) > 0).map(([k, v]) => ({
        assetID: parseInt(k, 10), quantityRequested: parseInt(v, 10) || 0
      })),
    };

    setLoading(true);
    try {
      await bookingApi.create(payload);
      setToast({ msg: 'Booking LE submitted! Management team will review shortly.', type: 'success' });
      setTimeout(() => navigate('/bookings'), 1800);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Failed to submit Booking LE. Please try again.', type: 'error' });
    } finally { setLoading(false); }
  }

  const today = formatDateForInput(new Date());
  const minTeacherBookingDate = isTeacherUser ? formatDateForInput(addDays(new Date(), 7)) : undefined;
  const minSelectableDate = isTeacherUser ? minTeacherBookingDate : today;
  const selectedArea = areas.find(a => a.eventAreaID === parseInt(form.eventAreaID));
  const pickerCells = useMemo(() => buildMonthGrid(pickerMonth), [pickerMonth]);

  function isSelectableDate(dateObj) {
    const ymd = formatDateForInput(dateObj);
    if (ymd < minSelectableDate) return false;
    if (isHolidayBlocked(ymd, holidayRules)) return false;
    return true;
  }

  function selectDate(dateObj) {
    const ymd = formatDateForInput(dateObj);
    if (!isSelectableDate(dateObj)) {
      setToast({ msg: 'Selected date is blocked. Please choose another date.', type: 'error' });
      return;
    }
    setForm((f) => ({ ...f, eventDate: ymd }));
    setShowDatePicker(false);
  }

  return (
    <div className="page-anim">
      <PageHead title="Booking LE" subtitle="Reserve an event area for your upcoming event" />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1.2fr', gap: '1.4rem', alignItems: 'start' }} className="booking-layout">

          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>

            {/* Step 1 */}
            <Card>
              <StepHeader num={1} color="var(--blue)" label="Event Details" />
              <div className="event-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
                <FormGroup label="Event Name *" full>
                  <Input value={form.eventName} onChange={e => setForm(f => ({ ...f, eventName: e.target.value }))} placeholder="e.g. Annual Sports Day Ceremony" required />
                </FormGroup>
                <FormGroup label="Campus *">
                  <Select
                    value={form.campusID}
                    onChange={e => setForm(f => ({ ...f, campusID: e.target.value }))}
                    disabled={isTeacherUser}
                  >
                    <option value="">-- Select Campus --</option>
                    {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup label="Event Area *">
                  <Select value={form.eventAreaID} onChange={e => setForm(f => ({ ...f, eventAreaID: e.target.value }))}>
                    <option value="">-- Select Area --</option>
                    {areas.map(a => <option key={a.eventAreaID} value={a.eventAreaID}>{a.areaName} {a.capacity ? `(Cap: ${a.capacity})` : ''}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup label="Event Date *">
                  <div ref={datePickerRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowDatePicker((v) => !v)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 14,
                        fontFamily: 'var(--font-body)',
                        color: form.eventDate ? 'var(--text1)' : 'var(--text3)',
                        background: 'var(--white)',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      {form.eventDate ? formatDisplayDate(form.eventDate) : 'Select date'}
                    </button>
                    {showDatePicker && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        zIndex: 50,
                        width: 'min(320px, calc(100vw - 48px))',
                        maxWidth: '100%',
                        background: 'white',
                        border: '1.5px solid var(--border)',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px rgba(26,37,53,.16)',
                        padding: 10
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <button type="button" onClick={() => setPickerMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} style={calendarNavBtnStyle}>{'<'}</button>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>
                            {pickerMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                          </div>
                          <button type="button" onClick={() => setPickerMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} style={calendarNavBtnStyle}>{'>'}</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 6 }}>
                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                            <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'var(--text3)', fontWeight: 700 }}>{d}</div>
                          ))}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
                          {pickerCells.map((cellDate, idx) => {
                            const ymd = formatDateForInput(cellDate);
                            const isSameMonth = cellDate.getMonth() === pickerMonth.getMonth();
                            const disabled = !isSelectableDate(cellDate);
                            const selected = form.eventDate === ymd;
                            const holiday = isHolidayBlocked(ymd, holidayRules);
                            return (
                              <button
                                key={`${ymd}-${idx}`}
                                type="button"
                                onClick={() => selectDate(cellDate)}
                                disabled={disabled}
                                title={holiday ? 'Holiday (blocked)' : ''}
                                style={{
                                  height: 34,
                                  borderRadius: 8,
                                  border: selected ? '1.5px solid var(--blue)' : '1px solid var(--border)',
                                  background: selected ? 'var(--blue-light)' : holiday ? '#FDEAEA' : 'white',
                                  color: !isSameMonth ? '#b3bcc8' : holiday ? '#b01a1a' : 'var(--text1)',
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                  opacity: disabled && !holiday ? 0.5 : 1,
                                  fontWeight: selected ? 700 : 500,
                                }}
                              >
                                {cellDate.getDate()}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                          Holidays and blocked dates are disabled.
                        </div>
                      </div>
                    )}
                  </div>
                  <input type="hidden" value={form.eventDate} required />
                  {isTeacherUser && (
                    <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text3)' }}>
                      Teachers cannot book in the next 6 days.
                    </div>
                  )}
                  {!holidayLoading && (
                    <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text3)' }}>
                      Holidays are blocked for booking.
                    </div>
                  )}
                </FormGroup>
                <FormGroup label="Expected Attendees">
                  <Input type="number" min={1} value={form.expectedAttendees} onChange={e => setForm(f => ({ ...f, expectedAttendees: e.target.value }))} placeholder="e.g. 200" />
                </FormGroup>
                <FormGroup label="Purpose" full>
                  <Textarea value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Brief description of the event..." speechToText style={{ minHeight: 150 }} />
                </FormGroup>
              </div>
            </Card>

            {/* Step 2 */}
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <StepHeader num={2} color="var(--green)" label="Time Slots" />
                <span style={{ fontSize: 11, color: 'var(--text2)' }}>8 AM - 10 PM | 30-minute slots</span>
              </div>
              {!form.eventAreaID || !form.eventDate ? (
                <div style={{ padding: '20px', background: 'var(--bg2)', borderRadius: 8, textAlign: 'center', color: 'var(--text3)', fontSize: 13, marginTop: 12 }}>
                  Select an event area and date to see available slots
                </div>
              ) : loadingSlots ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', gap: 8 }}><Spinner /> <span style={{ color: 'var(--text2)', fontSize: 13 }}>Checking availability...</span></div>
              ) : availableSlots.length === 0 ? (
                <div style={{ padding: '20px', background: 'var(--bg2)', borderRadius: 8, textAlign: 'center', color: 'var(--text3)', fontSize: 13, marginTop: 12 }}>
                  No slots available for selected date (holiday or unavailable).
                </div>
              ) : (
                <>
                  <Legend />
                  <div className="slot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 8 }}>
                    {availableSlots.map(slotInfo => {
                      const slot = slotInfo.slotStart;
                      const status = slotStatus[slot] || slotInfo.status || 'Available';
                      const sel    = selectedSlots.includes(slot);
                      const taken  = status === 'Taken';
                      const end    = slotInfo.slotEnd || addMinutesToTime(slot, SLOT_STEP_MINUTES);
                      return (
                        <button type="button" key={slot} onClick={() => toggleSlot(slot)} disabled={taken} style={{
                          padding: '9px 4px', border: `1.5px solid ${sel ? 'var(--blue)' : taken ? '#f5c6c2' : 'var(--border)'}`,
                          borderRadius: 8, fontSize: 12, fontWeight: 700, textAlign: 'center', cursor: taken ? 'not-allowed' : 'pointer',
                          background: sel ? 'var(--blue)' : taken ? '#fff0f0' : 'var(--white)',
                          color: sel ? 'white' : taken ? 'var(--red)' : 'var(--text2)',
                          transition: 'all .15s', fontFamily: 'var(--font-body)'
                        }}>
                          {slot}<br />
                          <span style={{ fontSize: 9, opacity: .7 }}>{end}</span>
                          {taken && <><br /><span style={{ fontSize: 9, color: 'var(--red)' }}>Taken</span></>}
                        </button>
                      );
                    })}
                  </div>
                  {selectedSlots.length > 0 && (
                    <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--blue-light)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--blue-dark)' }}>
                      {selectedSlots.length} slot(s) selected: {startTime} - {endTime}
                    </div>
                  )}
                </>
              )}
            </Card>

            {/* Step 3 */}
            <Card>
              <StepHeader num={3} color="var(--orange)" label="Required Assets" />
              <span style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 12 }}>Optional - select equipment you need</span>
              {assets.map(a => (
                <div className="asset-row" key={a.assetID} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{a.assetName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Available: {a.totalQuantity}</div>
                  </div>
                  <div className="asset-controls" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <QtyBtn onClick={() => setQtyFor(a.assetID, -1)}>-</QtyBtn>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={qty[a.assetID] || 0}
                      onChange={e => setQtyManual(a.assetID, e.target.value)}
                      style={{
                        width: 74,
                        padding: '6px 8px',
                        border: '1.5px solid var(--border)',
                        borderRadius: 8,
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: 14
                      }}
                    />
                    <QtyBtn onClick={() => setQtyFor(a.assetID, 1)}>+</QtyBtn>
                  </div>
                </div>
              ))}
            </Card>

            <div className="booking-actions" style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" variant="primary" disabled={loading} style={{ flex: 1, justifyContent: 'center', padding: '13px', fontSize: 15 }}>
                {loading ? <Spinner size={18} color="white" /> : 'Submit Booking LE Request'}
              </Btn>
              <Btn variant="ghost" onClick={() => navigate('/bookings')}>Cancel</Btn>
            </div>
          </div>

          {/* Summary panel */}
          <div className="booking-summary-panel" style={{ position: 'sticky', top: 80 }}>
            <Card>
              <div style={{ fontFamily: 'var(--font-head)', fontSize: 14, fontWeight: 700, marginBottom: '1rem', paddingBottom: '.8rem', borderBottom: '1px solid var(--border)' }}>Booking LE Summary</div>
              <SummaryRow label="Event"    value={form.eventName || '-'} />
              <SummaryRow label="Campus"   value={campuses.find(c => c.campusID === parseInt(form.campusID))?.campusName || '-'} />
              <SummaryRow label="Area"     value={selectedArea?.areaName || '-'} />
              <SummaryRow label="Date"     value={form.eventDate || '-'} />
              <SummaryRow label="Slots"    value={selectedSlots.length || '-'} />
              <SummaryRow label="Duration" value={durationLabel} />
              {selectedSlots.length > 0 && <SummaryRow label="Time" value={`${startTime} - ${endTime}`} />}
              {Object.entries(qty).filter(([, v]) => (parseInt(v, 10) || 0) > 0).map(([k, v]) => (
                <SummaryRow key={k} label={assets.find(a => a.assetID === parseInt(k))?.assetName || ''} value={`x${v}`} />
              ))}
              <div style={{ marginTop: '1rem', padding: 10, background: 'var(--orange-light)', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#B55A00' }}>Requires Approval</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>Management will review and notify you by email</div>
              </div>
            </Card>
          </div>
        </div>
      </form>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <style>{`
        @media(max-width:900px){
          .booking-layout{grid-template-columns:1fr!important}
          .booking-summary-panel{position:static!important}
        }
        @media(max-width:1024px){
          .event-details-grid{grid-template-columns:1fr!important; gap:10px!important}
        }
        @media(max-width:768px){
          .slot-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}
          .asset-row{flex-direction:column!important; align-items:flex-start!important; gap:10px!important}
          .asset-controls{width:100%; justify-content:flex-end}
          .booking-actions{flex-direction:column}
          .booking-actions > button{width:100%; justify-content:center}
        }
      `}</style>
    </div>
  );
}

function StepHeader({ num, color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.2rem' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: color, color: 'white', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{num}</div>
      <div style={{ fontFamily: 'var(--font-head)', fontSize: 15, fontWeight: 700 }}>{label}</div>
    </div>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: 12, flexWrap: 'wrap', marginTop: 8 }}>
      {[['var(--blue)','Selected'], ['var(--bg2)','Available'], ['#fff0f0','Taken']].map(([bg, l]) => (
        <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, background: bg, border: '1px solid var(--border)', borderRadius: 3, display: 'inline-block' }} />{l}
        </span>
      ))}
    </div>
  );
}

function QtyBtn({ children, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--bg2)', border: '1.5px solid var(--border)', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
      {children}
    </button>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 8 }}>
      <span style={{ color: 'var(--text2)' }}>{label}</span>
      <span style={{ fontWeight: 700, textAlign: 'right', maxWidth: '58%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateForInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeDateYmd(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('-');
    return `${yyyy}-${mm}-${dd}`;
  }
  return '';
}

function isHolidayBlocked(dateValue, rules) {
  const ymd = normalizeDateYmd(dateValue);
  if (!ymd) return false;

  const openDates = rules?.openDates || new Set();
  const closedDates = rules?.closedDates || new Set();

  if (openDates.has(ymd)) return false;
  if (closedDates.has(ymd)) return true;

  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const isSunday = dt.getDay() === 0;
  const isSecondSaturday = dt.getDay() === 6 && d >= 8 && d <= 14;

  if (rules?.sundayClosed !== false && isSunday) return true;
  if (rules?.secondSaturdayClosed !== false && isSecondSaturday) return true;
  return false;
}

function formatDisplayDate(ymd) {
  const normalized = normalizeDateYmd(ymd);
  if (!normalized) return '';
  const [y, m, d] = normalized.split('-');
  return `${d}-${m}-${y}`;
}

function buildMonthGrid(monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const current = new Date(gridStart);
    current.setDate(gridStart.getDate() + i);
    cells.push(current);
  }
  return cells;
}

const calendarNavBtnStyle = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg2)',
  color: 'var(--text2)',
  cursor: 'pointer',
  fontWeight: 700,
};

function addMinutesToTime(time, minutesToAdd) {
  const [h, m] = time.split(':').map(Number);
  const total = (h * 60) + m + minutesToAdd;
  const nextHour = Math.floor(total / 60);
  const nextMin = total % 60;
  return `${String(nextHour).padStart(2, '0')}:${String(nextMin).padStart(2, '0')}`;
}

function generateHalfHourSlots(start, end) {
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const startTotal = (startH * 60) + startM;
  const endTotal = (endH * 60) + endM;
  const slots = [];

  for (let current = startTotal; current < endTotal; current += SLOT_STEP_MINUTES) {
    const hh = String(Math.floor(current / 60)).padStart(2, '0');
    const mm = String(current % 60).padStart(2, '0');
    slots.push(`${hh}:${mm}`);
  }

  return slots;
}

const DEMO_AREAS  = [
  { eventAreaID:1, campusID:1, areaName:'Multi-Purpose Hall', capacity:500 },
  { eventAreaID:2, campusID:1, areaName:'Conference Room A',  capacity:30  },
  { eventAreaID:3, campusID:1, areaName:'Conference Room B',  capacity:20  },
  { eventAreaID:4, campusID:1, areaName:'Computer Lab',       capacity:40  },
  { eventAreaID:5, campusID:2, areaName:'Multi-Purpose Hall', capacity:400 },
  { eventAreaID:6, campusID:2, areaName:'Seminar Hall',       capacity:100 },
];
const DEMO_ASSETS = [
  { assetID:1, assetName:'Chairs',     totalQuantity:200 },
  { assetID:2, assetName:'Microphone', totalQuantity:4   },
  { assetID:3, assetName:'TV Trolley', totalQuantity:2   },
  { assetID:4, assetName:'Projector',  totalQuantity:3   },
  { assetID:5, assetName:'Podium',     totalQuantity:2   },
];




